// +----------------------------------------------------------------------
// | EB [ EB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016-2026 https://www.eb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed EB并不是自由软件，未经许可不能去掉EB相关版权
// +----------------------------------------------------------------------
// | Author: EB Team <admin@eb.com>
// +----------------------------------------------------------------------
import { h } from 'vue';
import { ElNotification } from 'element-plus';
import { io, Socket } from 'socket.io-client';
import SettingMer from '@/libs/settingMer';
import router from '@/router';
import { roterPre } from '@/settings';

interface NoticePayload extends Record<string, unknown> {
  id?: number | string;
  title?: string;
  message?: string;
  type?: number | string;
}

interface WsNoticeData {
  status?: number;
  type?: string;
  data?: {
    type?: string;
    data?: NoticePayload;
  };
}

/** 与 Nest `path: '/socket.io'` + 网关 `/api/socket.io` 对齐 */
const SOCKET_IO_PATH = '/api/socket.io';

/** 本地 dev：登录后稍等再连，避免 Nest watch 重启瞬间打满 Vite proxy */
const DEV_INITIAL_CONNECT_DELAY_MS = 1500;
/** 探测 API Socket 最长等待（Nest 热重启通常几秒内恢复） */
const DEV_API_READY_MAX_WAIT_MS = 20000;
/** connect_error / 探测失败日志节流 */
const DEV_LOG_THROTTLE_MS = 30000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isLocalDevHost(): boolean {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

/** 解析 Socket.IO 连接根地址（http/https，非 ws://） */
function resolveNoticeSocketBase(): string {
  const isLocalhost = isLocalDevHost();
  if (import.meta.env.DEV && isLocalhost) {
    // 本地 dev：走 Vite 同源 + /api/socket.io 代理 → Nest :3000
    return window.location.origin;
  }

  const raw = SettingMer.wsSocketUrl || '';
  const httpUrl = raw.replace(/^ws:\/\//, 'http://').replace(/^wss:\/\//, 'https://');
  if (httpUrl.startsWith('http://') || httpUrl.startsWith('https://')) {
    try {
      const url = new URL(httpUrl.split('?')[0]);
      return url.origin;
    } catch {
      /* fall through */
    }
  }
  return window.location.origin;
}

let lastDevLogAt = 0;

function devLogThrottled(message: string, detail?: string) {
  if (!import.meta.env.DEV) return;
  const now = Date.now();
  if (now - lastDevLogAt < DEV_LOG_THROTTLE_MS) return;
  lastDevLogAt = now;
  if (detail) {
    console.warn(`${message}（约 ${DEV_LOG_THROTTLE_MS / 1000}s 内只提示一次）`, detail);
  } else {
    console.warn(`${message}（约 ${DEV_LOG_THROTTLE_MS / 1000}s 内只提示一次）`);
  }
}

/**
 * 本地 dev：轮询探测 Socket.IO 是否可连（Nest 未启动 / watch 重启时避免立刻重连风暴）
 */
async function waitForApiSocketReady(isCancelled?: () => boolean): Promise<boolean> {
  if (!import.meta.env.DEV || !isLocalDevHost()) {
    return true;
  }

  const probeUrl = `${window.location.origin}${SOCKET_IO_PATH}/?EIO=4&transport=polling`;
  const startedAt = Date.now();
  let delayMs = 800;

  while (Date.now() - startedAt < DEV_API_READY_MAX_WAIT_MS) {
    if (isCancelled?.()) {
      return false;
    }
    try {
      const res = await fetch(probeUrl, {
        method: 'GET',
        cache: 'no-store',
        signal: AbortSignal.timeout(2500),
      });
      // 有 HTTP 响应即表示 Nest 已在监听（握手参数不完整时也可能 400）
      if (res.ok || res.status === 400) {
        return true;
      }
    } catch {
      /* ECONNREFUSED：API 未就绪 */
    }
    if (isCancelled?.()) {
      return false;
    }
    await sleep(delayMs);
    delayMs = Math.min(Math.round(delayMs * 1.6), 5000);
  }

  devLogThrottled('[notice] API Socket 暂不可用，仍将尝试连接（请确认 tita_api 已启动）');
  return false;
}

function createNoticeSocket(token: string): Socket {
  const baseUrl = resolveNoticeSocketBase();
  if (import.meta.env.DEV) {
    // console.log('[notice] Socket.IO 准备连接:', baseUrl, SOCKET_IO_PATH, {
      // type: 'mer',
      // token: token ? '***' : 'missing',
    // });
  }
  return io(baseUrl, {
    path: SOCKET_IO_PATH,
    query: { type: 'mer', token },
    transports: ['polling', 'websocket'],
    autoConnect: false,
    reconnection: true,
    // Nest 热重启期间拉长重连间隔，减少 Vite proxy error 刷屏
    reconnectionAttempts: Infinity,
    reconnectionDelay: 3000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
    timeout: 20000,
  });
}

function handleNoticeMessage(data: WsNoticeData) {
  if (data.status === 200) {
    // 原 vm.$emit(data.data.status, data.data.result) 依赖 Vue2 实例
  }
  if (data.type !== 'notice') return;

  const noticeType = data.data?.type;
  const noticeData = data.data?.data;
  const title = noticeData?.title;
  const messageText = noticeData?.message;
  const id = noticeData?.id;

  ElNotification({
    title: title || '消息',
    message: h(
      'a',
      {
        style: { color: 'teal', cursor: 'pointer' },
        onClick: () => {
          if (!noticeData) return;
          if (noticeType === 'min_stock' || noticeType === 'product') {
            router.push({ path: `${roterPre}/product/list` });
          } else if (noticeType === 'reply') {
            router.push({ path: `${roterPre}/product/reviews` });
          } else if (noticeType === 'product_success') {
            router.push({ path: `${roterPre}/product/list?id=${id}&type=2` });
          } else if (noticeType === 'product_fail') {
            router.push({ path: `${roterPre}/product/list?id=${id}&type=7` });
          } else if (noticeType === 'product_seckill_success') {
            router.push({ path: `${roterPre}/marketing/seckill/list?id=${id}&type=2` });
          } else if (noticeType === 'product_seckill_fail') {
            router.push({ path: `${roterPre}/marketing/seckill/list?id=${id}&type=7` });
          } else if (noticeType === 'new_order') {
            router.push({ path: `${roterPre}/order/list?id=${id}` });
          } else if (noticeType === 'new_refund_order') {
            router.push({ path: `${roterPre}/order/refund?id=${id}` });
          } else if (noticeType === 'product_presell_success') {
            router.push({
              path: `${roterPre}/marketing/presell/list?id=${id}&type=${noticeData.type}&status=1`,
            });
          } else if (noticeType === 'product_presell_fail') {
            router.push({
              path: `${roterPre}/marketing/presell/list?id=${id}&type=${noticeData.type}&status=-1`,
            });
          } else if (noticeType === 'product_group_success') {
            router.push({ path: `${roterPre}/marketing/combination/combination_goods?id=${id}&status=1` });
          } else if (noticeType === 'product_group_fail') {
            router.push({ path: `${roterPre}/marketing/combination/combination_goods?id=${id}&status=-1` });
          } else if (noticeType === 'product_assist_success') {
            router.push({ path: `${roterPre}/marketing/assist/list?id=${id}&status=1` });
          } else if (noticeType === 'product_assist_fail') {
            router.push({ path: `${roterPre}/marketing/assist/list?id=${id}&status=-1` });
          } else if (noticeType === 'broadcast_status_success') {
            router.push({ path: `${roterPre}/marketing/studio/list?id=${id}&status=1` });
          } else if (noticeType === 'broadcast_status_fail') {
            router.push({ path: `${roterPre}/marketing/studio/list?id=${id}&status=-1` });
          } else if (noticeType === 'goods_status_success') {
            router.push({ path: `${roterPre}/marketing/broadcast/list?id=${id}&status=1` });
          } else if (noticeType === 'goods_status_fail') {
            router.push({ path: `${roterPre}/marketing/broadcast/list?id=${id}&status=-1` });
          }
        },
      },
      messageText,
    ),
  });
}

function notice(token: string): () => void {
  let disposed = false;
  const socket = createNoticeSocket(token);
  let ping: ReturnType<typeof setInterval> | undefined;

  function send(type: string, data?: unknown) {
    socket.emit('message', { type, data });
  }

  socket.on('connect', () => {
    if (import.meta.env.DEV) {
      // console.log('[notice] Socket.IO 已连接');
    }
    ping = setInterval(() => {
      send('ping');
    }, 10000);
  });

  socket.on('message', (payload: WsNoticeData | string) => {
    try {
      const data = typeof payload === 'string' ? (JSON.parse(payload) as WsNoticeData) : payload;
      handleNoticeMessage(data);
    } catch (e) {
      console.warn('[notice] parse message failed:', e);
    }
  });

  socket.on('connect_error', (err: Error) => {
    devLogThrottled('[notice] Socket.IO connect_error', err.message);
  });

  socket.on('disconnect', () => {
    if (ping) {
      clearInterval(ping);
      ping = undefined;
    }
  });

  void (async () => {
    await waitForApiSocketReady(() => disposed);
    if (disposed) return;
    socket.connect();
  })();

  return function () {
    disposed = true;
    if (ping) clearInterval(ping);
    socket.disconnect();
  };
}

/** 当前 WebSocket 通知连接的关闭函数（对应 EB main 实例上的 closeNotice） */
let noticeCloser: (() => void) | null = null;
let startNoticeTimer: ReturnType<typeof setTimeout> | null = null;

export function startNotice(token: string) {
  closeNotice();

  const delayMs =
    import.meta.env.DEV && isLocalDevHost() ? DEV_INITIAL_CONNECT_DELAY_MS : 0;

  startNoticeTimer = setTimeout(() => {
    startNoticeTimer = null;
    noticeCloser = notice(token);
  }, delayMs);
}

export function closeNotice() {
  if (startNoticeTimer) {
    clearTimeout(startNoticeTimer);
    startNoticeTimer = null;
  }
  noticeCloser?.();
  noticeCloser = null;
}

export default notice;
